import React, { useState, useMemo } from 'react';
import { Modal, Select, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import {
  SpaceOption,
  SpaceRelation,
  SpaceType,
  ASSIGN_USER_TO_SPACE,
  AssignUserToSpaceVariables,
  AssignUserToSpaceResponse,
  AUTH_API_URL,
} from './SpaceAssignments';
import { GET_SITE_HIERARCHY } from '@/hooks/useSiteOptions';
import { SiteOption } from '@/hooks/useSiteHierarchyFilters';

const { Option } = Select;

// Flatten site hierarchy into SpaceOption[] for the dropdown
function flattenHierarchyToSpaceOptions(sites: SiteOption[]): SpaceOption[] {
  const options: SpaceOption[] = [];

  for (const site of sites) {
    options.push({
      id: site.id,
      name: site.name,
      level: 'site',
    });

    for (const building of site.buildings || []) {
      options.push({
        id: building.id,
        name: `${site.name} > ${building.name}`,
        level: 'building',
        parentId: site.id,
      });

      for (const floor of building.floors || []) {
        options.push({
          id: floor.id,
          name: `${site.name} > ${building.name} > ${floor.name}`,
          level: 'floor',
          parentId: building.id,
        });

        for (const room of floor.rooms || []) {
          options.push({
            id: room.id,
            name: `${site.name} > ${building.name} > ${floor.name} > ${room.name}`,
            level: 'room',
            parentId: floor.id,
          });
        }

        for (const zone of floor.zones || []) {
          options.push({
            id: zone.id,
            name: `${site.name} > ${building.name} > ${floor.name} > ${zone.name}`,
            level: 'zone',
            parentId: floor.id,
          });
        }
      }
    }
  }

  return options;
}

interface AddSpaceModalProps {
  open: boolean;
  userId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export const AddSpaceModal: React.FC<AddSpaceModalProps> = ({
  open,
  userId,
  onCancel,
  onSuccess,
}) => {
  const [spaceId, setSpaceId] = useState<string>('');
  const [relation, setRelation] = useState<SpaceRelation>('can_view');

  // Fetch real site hierarchy from main Butlr API (not auth API)
  const { data: hierarchyData, loading: hierarchyLoading } = useQuery(GET_SITE_HIERARCHY, {
    skip: !open, // Only fetch when modal is open
  });

  // Flatten hierarchy into dropdown options
  const availableSpaces = useMemo(() => {
    const sites = (hierarchyData?.sites?.data || []) as SiteOption[];
    return flattenHierarchyToSpaceOptions(sites);
  }, [hierarchyData]);

  // Find the space level from its ID
  const getSpaceType = (id: string): SpaceType => {
    const space = availableSpaces.find((s) => s.id === id);
    return space?.level || 'room';
  };

  const [assignToSpace, { loading }] = useMutation<
    AssignUserToSpaceResponse,
    AssignUserToSpaceVariables
  >(ASSIGN_USER_TO_SPACE, {
    context: { uri: AUTH_API_URL },
  });

  const handleAdd = async () => {
    if (!spaceId) {
      message.error('Please select a space');
      return;
    }

    try {
      await assignToSpace({
        variables: {
          input: {
            user_id: userId,
            space_type: getSpaceType(spaceId),
            space_id: spaceId,
            relation,
          },
        },
      });
      message.success('Space assignment added');
      // Reset form state
      setSpaceId('');
      setRelation('can_view');
      // Close modal and notify parent to refresh
      onCancel();
      onSuccess();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add space assignment';
      message.error(errorMessage);
    }
  };

  const handleClose = () => {
    setSpaceId('');
    setRelation('can_view');
    onCancel();
  };

  return (
    <Modal
      title="Assign Space Access"
      open={open}
      onCancel={handleClose}
      onOk={handleAdd}
      okText="Add"
      cancelText="Cancel"
      confirmLoading={loading || hierarchyLoading}
      okButtonProps={{ className: 'bg-black hover:bg-gray-800' }}
      width={480}
    >
      <div className="bg-gray-50 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-2">
          <Select
            placeholder={hierarchyLoading ? 'Loading spaces...' : 'Please select a space'}
            value={spaceId || undefined}
            onChange={(value) => setSpaceId(value)}
            className="flex-1"
            loading={hierarchyLoading}
            disabled={hierarchyLoading}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {availableSpaces.map((space) => (
              <Option key={space.id} value={space.id}>
                {space.name} ({space.level})
              </Option>
            ))}
          </Select>
          <Select value={relation} onChange={(value) => setRelation(value)} style={{ width: 120 }}>
            <Option value="can_view">Can View</Option>
            <Option value="can_edit">Can Edit</Option>
          </Select>
        </div>
      </div>
      <p className="text-gray-500 text-sm mt-2">Assign at least one space and role.</p>
    </Modal>
  );
};

export default AddSpaceModal;
